import { Edit3, MessageSquare, Trash2 } from 'lucide-react';
import { ChannelBadge } from './DealCard';
import type { Deal, StageItem } from './types';

interface DealsTableProps {
  deals: Deal[];
  stages: StageItem[];
  onStageChange: (dealId: number, stageName: string) => void;
  onEdit: (deal: Deal) => void;
  onDelete: (dealId: number) => void;
  onGoToChat: (conversationId?: number | null) => void;
}

export default function DealsTable({
  deals,
  stages,
  onStageChange,
  onEdit,
  onDelete,
  onGoToChat,
}: DealsTableProps) {
  return (
    <div className="table-view-container">
      <table className="deals-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Заявка</th>
            <th>Клиент / Канал</th>
            <th>Этап воронки</th>
            <th>Сумма</th>
            <th>Состав заказа</th>
            <th>Дата</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {deals.length === 0 ? (
            <tr>
              <td colSpan={8} className="table-empty">Заявки не найдены</td>
            </tr>
          ) : (
            deals.map((deal) => {
              const handleEdit = () => onEdit(deal);
              const handleDelete = () => onDelete(deal.id);
              const handleChat = () => onGoToChat(deal.conversationId);
              const handleStageSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
                onStageChange(deal.id, event.target.value);
              };
              const showOriginal = Boolean(deal.originalAmount && deal.originalAmount > deal.amount);
              const hasDiscount = Boolean(deal.discountPercent && deal.discountPercent > 0);

              return (
                <tr key={deal.id}>
                  <td className="td-id">#{deal.id}</td>
                  <td className="td-title">
                    <button type="button" className="deal-title-link" onClick={handleEdit}>
                      {deal.title}
                    </button>
                  </td>
                  <td>
                    <div className="td-client">
                      <b>{deal.conversation?.contactName || '—'}</b>
                      <div className="td-client-sub">
                        <ChannelBadge deal={deal} />
                        {deal.conversation?.contactPhone && <span>{deal.conversation.contactPhone}</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <select
                      className="table-stage-select"
                      value={deal.stage}
                      aria-label={`Изменить этап заявки «${deal.title || `№${deal.id}`}»`}
                      onChange={handleStageSelect}
                    >
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>{stage.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="td-amount">
                    {showOriginal && (
                      <div className="td-amount-original">
                        {(deal.originalAmount || 0).toLocaleString('ru-RU')} ₽
                      </div>
                    )}
                    <div className="td-amount-final">
                      {(deal.amount || 0).toLocaleString('ru-RU')} ₽
                      {hasDiscount && <span className="td-amount-discount">(-{deal.discountPercent}%)</span>}
                    </div>
                  </td>
                  <td className="td-items">
                    {deal.items && deal.items.length > 0 ? (
                      <span>{deal.items.map((item) => `${item.name} (x${item.qty})`).join(', ')}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="td-date">
                    {new Date(deal.createdAt).toLocaleDateString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <div className="td-actions">
                      {deal.conversationId && (
                        <button type="button" className="td-act-btn chat" onClick={handleChat} title="Открыть чат">
                          <MessageSquare size={14} /> Чат
                        </button>
                      )}
                      <button type="button" className="td-act-btn edit" onClick={handleEdit} title="Редактировать">
                        <Edit3 size={14} />
                      </button>
                      <button type="button" className="td-act-btn delete" onClick={handleDelete} title="Удалить">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
